import { useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import FactCheck from "@mui/icons-material/FactCheck";
import Visibility from "@mui/icons-material/Visibility";
import { Alert, Button, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import {
  EpisodicEpaFilingFieldsFragment,
  EpisodicEventItemFragment,
  WasteEpisodicEventNoticeKind,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate, formatContainerDateLong } from "../Containers/containerDisplay";
import {
  CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION,
  DELETE_EPISODIC_EVENT_NOTICE_MUTATION,
} from "./api";
import { EPA_FILING_VERIFIED_LABEL } from "./epaFilingDisplay";
import { EpaFilingRecordDialog } from "./EpaFilingRecordDialog";
import {
  CONTACT_METHOD_LABELS,
  contactDueDate,
  episodicSubmissionRoute,
  EpisodicNotice,
  filedSubmission,
  NO_VALUE_PLACEHOLDER,
  openSubmission,
  resolveContactObligation,
  resolveFormObligation,
} from "./episodicDisplay";
import { NoticeProofDocument } from "./noticeProof";
import { RecordNoticeDialog } from "./RecordNoticeDialog";

/**
 * "View proof" for a recorded notice's attached document: fetches a fresh
 * signed inline link on click (the row only carries the document's id/title)
 * and opens it in the shared full-screen preview rather than a download.
 */
function ViewProofButton({ document }: { document: NoticeProofDocument }) {
  const alerts = useAlerts();
  const [getDocumentDownloadLink, { loading }] = useDocumentDownloadLink();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleView = async () => {
    const { data } = await getDocumentDownloadLink({
      variables: { id: document.id, inline: true },
    });
    if (data?.getDocumentDownloadLink) {
      setPreviewUrl(data.getDocumentDownloadLink);
    } else {
      alerts.error("Failed to open the proof document");
    }
  };

  return (
    <>
      <Tooltip title="View proof">
        <IconButton size="small" disabled={loading} onClick={handleView} aria-label="View proof">
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>
      <FullScreenDocumentPreview
        open={previewUrl != null}
        onClose={() => setPreviewUrl(null)}
        fileUrl={previewUrl ?? ""}
        fileExtension={document.fileExtension ?? undefined}
        title={document.title}
      />
    </>
  );
}

/**
 * "View EPA record" for a notice whose filing was verified against EPA's
 * own records: opens the read-only `EpaFilingRecordDialog` for the snapshot
 * carried on the notice.
 */
function ViewEpaRecordButton({ filing }: { filing: EpisodicEpaFilingFieldsFragment }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="View EPA record">
        <IconButton size="small" onClick={() => setOpen(true)} aria-label="View EPA record">
          <FactCheck fontSize="small" />
        </IconButton>
      </Tooltip>
      <EpaFilingRecordDialog filing={filing} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/**
 * Delete-with-confirm for an already-recorded notice, shared by both
 * `ContactObligationRow`'s `recorded` state and `FormObligationRow`'s
 * `filedExternally` state. Available regardless of the event's status — the
 * notice obligation stays correctable after the window ends.
 */
function useNoticeRemoval(eventId: string, kind: WasteEpisodicEventNoticeKind) {
  const alerts = useAlerts();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteNotice, { loading }] = useMutation(DELETE_EPISODIC_EVENT_NOTICE_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  const handleRemove = () =>
    deleteNotice({
      variables: { eventId, kind },
      onCompleted: () => {
        alerts.success("Record removed");
        setConfirmOpen(false);
      },
      onError: () => {
        alerts.error("An error occurred while removing the record");
      },
    });

  return {
    confirmOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    loading,
    handleRemove,
  };
}

/**
 * "Edit record" / "Remove record" actions for a recorded notice, rendered as
 * a recorded alert's `action`. Edit reopens `RecordNoticeDialog` prefilled
 * with the notice's own date/method — saving is the same upsert, which
 * overwrites the existing notice by kind. Remove fires
 * `DELETE_EPISODIC_EVENT_NOTICE_MUTATION` behind a small confirm.
 */
function RecordedNoticeActions({
  event,
  kind,
  notice,
}: {
  event: EpisodicEventItemFragment;
  kind: WasteEpisodicEventNoticeKind;
  notice: EpisodicNotice;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const { confirmOpen, openConfirm, closeConfirm, loading, handleRemove } = useNoticeRemoval(
    event.id,
    kind
  );

  return (
    <Stack direction="row" spacing={1}>
      {notice.document && <ViewProofButton document={notice.document} />}
      {notice.epaFiling && <ViewEpaRecordButton filing={notice.epaFiling} />}
      <Tooltip title="Edit record">
        <IconButton size="small" onClick={() => setEditOpen(true)} aria-label="Edit record">
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove record">
        <IconButton size="small" onClick={openConfirm} aria-label="Remove record">
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
      <RecordNoticeDialog
        eventId={event.id}
        kind={kind}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        facilityId={event.facilityId}
        initialDate={notice.noticeDate}
        initialMethod={notice.method}
        initialDocument={notice.document}
        initialEpaFiling={notice.epaFiling ?? null}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={() => handleRemove()}
        loading={loading}
        header="Remove this record?"
        info="The obligation will show as unmet until you record it again."
        confirmText="Remove"
      />
    </Stack>
  );
}

/**
 * The unplanned-only 72-hour EPA-contact row: absent for planned events
 * (`resolveContactObligation` returns `null`). The card never renders a
 * judgment here — every outcome is either a plain statement of fact
 * (`recorded`, `satisfiedByForm`) or a neutral prompt (`due`/`overdue`
 * collapse to the same info alert); save-time timeliness warnings still live
 * in `RecordNoticeDialog`, and staff-facing lateness alerts are a separate,
 * later surface (ENG-16966).
 */
function ContactObligationRow({ event }: { event: EpisodicEventItemFragment }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const state = resolveContactObligation(event);

  if (!state) {
    return null;
  }

  if (state.kind === "recorded") {
    const methodLabel = state.notice.method
      ? CONTACT_METHOD_LABELS[state.notice.method]
      : NO_VALUE_PLACEHOLDER;
    return (
      <Alert
        severity="success"
        action={
          <RecordedNoticeActions
            event={event}
            kind={WasteEpisodicEventNoticeKind.EpaContact}
            notice={state.notice}
          />
        }
      >
        {`EPA contacted · ${formatContainerDate(state.notice.noticeDate)} · ${methodLabel}`}
      </Alert>
    );
  }

  if (state.kind === "satisfiedByForm") {
    return (
      <Alert severity="success">EPA contact satisfied — Form 8700-12 filed within 72 hours</Alert>
    );
  }

  return (
    <>
      <Alert
        severity="info"
        action={
          <Button size="small" onClick={() => setDialogOpen(true)}>
            Record contact
          </Button>
        }
      >
        {`Notify EPA by phone, email, or fax by ${formatContainerDate(contactDueDate(event.startDate))} — then record it here.`}
      </Alert>
      <RecordNoticeDialog
        eventId={event.id}
        kind={WasteEpisodicEventNoticeKind.EpaContact}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        facilityId={event.facilityId}
      />
    </>
  );
}

/**
 * A completed filing: a plain statement of fact linking to the submission
 * that filed it. No timeliness judgment is rendered here — save-time
 * warnings still live in the capture dialogs/sections, and a staff-facing
 * lateness alert is a separate, later surface (ENG-16966).
 */
function FiledFormAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const filed = filedSubmission(event);
  return (
    <Alert severity="success">
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
        <span>EPA notified</span>
        {filed && (
          <Button
            component={RouterLink}
            to={episodicSubmissionRoute(tenantId, filed.id, stepperEnabled)}
            target="_blank"
            rel="noopener"
            size="small"
          >
            View submission
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

/** A submission that's been created but not yet filed: links to it too. */
function SubmissionInProgressAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const open = openSubmission(event);
  return (
    <Alert
      severity="info"
      action={
        open && (
          <Button
            component={RouterLink}
            to={episodicSubmissionRoute(tenantId, open.id, stepperEnabled)}
            target="_blank"
            rel="noopener"
            size="small"
          >
            View submission
          </Button>
        )
      }
    >
      Notification submission in progress
    </Alert>
  );
}

/**
 * Nothing's been filed yet: prompts a submission, with a secondary path for
 * recording a filing that already happened outside Encamp. The copy no
 * longer claims a fixed 72-hour deadline for the form itself — only the
 * EPA-contact obligation has one — but an unplanned event still gets a nudge
 * to file promptly once that contact is made.
 */
function NotFiledFormAlert({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Creating the submission is staff-only; customers record an outside filing.
  const { isStaff } = useCurrentUser();
  const [createSubmission, { loading }] = useMutation(CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents"],
  });

  const handleCreateSubmission = async () => {
    const result = await createSubmission({ variables: { eventId: event.id } });
    const submissionId = result.data?.createEpisodicEventSubmission;
    if (submissionId) {
      // New tab so the event page (and its manual-testing context) stays put;
      // the refetch above keeps this page's row state current regardless.
      window.open(episodicSubmissionRoute(tenantId, submissionId, stepperEnabled), "_blank");
    }
  };

  return (
    <>
      <Alert
        severity="info"
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setDialogOpen(true)}>
              Record external filing
            </Button>
            {isStaff && (
              <Button size="small" disabled={loading} onClick={handleCreateSubmission}>
                Create submission
              </Button>
            )}
          </Stack>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Notification not filed — EPA Form 8700-12
        </Typography>
        <Typography variant="body2">
          {isStaff
            ? "Logging an episodic event doesn't file it. Create a submission to notify your regulator."
            : "Logging an episodic event doesn't file it. Encamp prepares and files the Form 8700-12 for you — contact Encamp Support to get it started. If it was already filed outside Encamp, record it here."}
        </Typography>
        {event.type === WasteEpisodicEventType.Unplanned && (
          <Typography variant="body2">
            There&apos;s no fixed deadline for the form — file promptly after your EPA contact.
          </Typography>
        )}
      </Alert>
      <RecordNoticeDialog
        eventId={event.id}
        kind={WasteEpisodicEventNoticeKind.FormFiledExternally}
        eventStartDate={event.startDate}
        eventType={event.type}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        facilityId={event.facilityId}
      />
    </>
  );
}

/**
 * The Form 8700-12 row, rendered for every event, dispatching on
 * `resolveFormObligation`'s priority: a completed filing wins over one still
 * in progress, then a manually recorded FORM_FILED_EXTERNALLY notice,
 * otherwise the event still needs a submission.
 */
function FormObligationRow({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  const state = resolveFormObligation(event);

  if (state.kind === "filed") {
    return <FiledFormAlert event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />;
  }
  if (state.kind === "submissionInProgress") {
    return (
      <SubmissionInProgressAlert
        event={event}
        tenantId={tenantId}
        stepperEnabled={stepperEnabled}
      />
    );
  }
  if (state.kind === "filedExternally") {
    const { epaFiling, document } = state.notice;
    // Proof is required for an outside filing, but a document can be deleted
    // out from under the notice (SetNull); an EPA record cannot vanish.
    const proofMissing = document == null && epaFiling == null;
    const receipt = epaFiling
      ? `received by EPA ${formatContainerDateLong(epaFiling.receiveDate)}`
      : formatContainerDate(state.notice.noticeDate);
    return (
      <Alert
        severity={proofMissing ? "warning" : "success"}
        action={
          <RecordedNoticeActions
            event={event}
            kind={WasteEpisodicEventNoticeKind.FormFiledExternally}
            notice={state.notice}
          />
        }
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{`Form 8700-12 filed outside Encamp · ${receipt}`}</span>
          {epaFiling && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={EPA_FILING_VERIFIED_LABEL}
            />
          )}
        </Stack>
        {proofMissing && (
          <Typography variant="body2">
            The proof document is missing — edit the record to re-attach it.
          </Typography>
        )}
      </Alert>
    );
  }
  return <NotFiledFormAlert event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />;
}

/**
 * Both of an episodic event's EPA notice obligations: the unplanned-only
 * 72-hour contact rule and the Form 8700-12 filing, each surfaced
 * independently rather than folded into one banner — an event can owe
 * either, both, or neither at any given moment.
 */
export function ObligationsSection({
  event,
  tenantId,
  stepperEnabled,
}: {
  event: EpisodicEventItemFragment;
  tenantId: string;
  stepperEnabled: boolean;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">EPA notice obligations</Typography>
      <ContactObligationRow event={event} />
      <FormObligationRow event={event} tenantId={tenantId} stepperEnabled={stepperEnabled} />
    </Stack>
  );
}
